import { FC, useContext, useEffect, useState } from 'react';
import MuiInsertLinkIcon from '@mui/icons-material/InsertLink';
import type { SxProps, Theme } from '@mui/material/styles';
import useTranslation from 'next-translate/useTranslation';

import { useMainModalSmallScreenLayout } from '@/components/MainModal/useMainModalSmallScreenLayout';
import { InviteStepCtx } from '@/components/Steps';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { selectCompanyId } from '@/redux/features/authSlice';
import { setDidCopyMagicLink } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import { Button } from '@/shared/Button';
import { Snackbar } from '@/shared/Snackbar';

import { useClipboard } from '../../hooks/useClipboard';

/** Dedupes hasInviteLink sync across remounts (e.g. compact footer portal vs inline). Cleared in tests. */
const dedupeHasInviteLinkSyncByCompanyId = new Map<string, true>();

/** @internal Unit tests only */
export function resetMagicLinkInviteExposureDedupeForTests() {
  dedupeHasInviteLinkSyncByCompanyId.clear();
}

type SnackbarState = {
  open: boolean;
  message: string;
}

type MagicLinkInvSimplifiedComponentProps = {
  /** When set, shown instead of default `magic_link.link_copied` after a successful copy */
  linkCopiedSuccessMessage?: string;
  /** When set, shown instead of default copy CTA label */
  copyButtonLabel?: string;
  /** Merged into copy button `sx` after defaults */
  copyButtonSx?: SxProps<Theme>;
  /** `sx` for the link icon (defaults to primary) */
  linkIconSx?: SxProps<Theme>;
};

export const MagicLinkInvSimplifiedComponent: FC<MagicLinkInvSimplifiedComponentProps> = ({
  linkCopiedSuccessMessage,
  copyButtonLabel,
  copyButtonSx,
  linkIconSx,
}) => {
  const { copyToClipboard } = useClipboard();
  const { t } = useTranslation();
  const isSmallScreen = useMainModalSmallScreenLayout();
  const { setDidCopyLink } = useContext(InviteStepCtx);

  const { company } = useAppSelector(state => state.auth);
  const companyId = useAppSelector(selectCompanyId);
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({ open: false, message: '' });
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!companyId) return;
    if (company?.companySettings?.custom?.hasInviteLink === true) return;
    if (dedupeHasInviteLinkSyncByCompanyId.has(companyId)) return;

    dedupeHasInviteLinkSyncByCompanyId.set(companyId, true);
    void Promise.resolve(
      CompanyRequests.putCompanySettings(companyId, { custom: { hasInviteLink: true }}),
    ).catch(() => {
      dedupeHasInviteLinkSyncByCompanyId.delete(companyId);
    });
  }, [companyId, company?.companySettings?.custom?.hasInviteLink]);

  const handleCopyLink = async () => {
    const { success } = await copyToClipboard(company?.inviteLink || '');
    const message =
      (success
        ? (linkCopiedSuccessMessage ?? t('onboarding:invite_members_step.magic_link.link_copied'))
        : t('onboarding:invite_members_step.magic_link.link_not_copied')) ?? '';

    if (success) {
      setDidCopyLink(true);
      dispatch(setDidCopyMagicLink(true));
    }
    setSnackbarState({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbarState((prevState) => ({ ...prevState, open: false }));
  };

  const copyLabel = copyButtonLabel ?? t('onboarding:invite_members_step.magic_link.copy_invite_link');

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        uppercase
        aria-label={ isSmallScreen ? copyLabel : undefined }
        onClick={ handleCopyLink }
        trackEvent={ 'Copied url on Invite Screen' }
        trackProps={{ invite_url: company?.inviteLink }}
        startIcon={ <MuiInsertLinkIcon sx={ linkIconSx ?? { color: 'primary.main' } } /> }
        sx={ [
          { fontSize: '14px', fontWeight: 500, lineHeight: '20px', letterSpacing: '0.75px' },
          isSmallScreen && {
            minWidth: 36,
            width: 36,
            padding: '8px',
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          },
          ...(copyButtonSx ? [copyButtonSx] : []),
        ] }
      >
        { !isSmallScreen && copyLabel }
      </Button>
      <Snackbar
        open={ snackbarState.open }
        onClose={ handleCloseSnackbar }
        message={ snackbarState.message }
      />
    </>
  );
};
